import { useEffect, useState } from "react";
import { api, STATUSES, statusMeta } from "@/lib/api";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import LeadFormDialog from "@/components/LeadFormDialog";
import { toast } from "sonner";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const nav = useNavigate();

  const load = async () => {
    const [l, c] = await Promise.all([api.get("/leads"), api.get("/campaigns")]);
    setLeads(l.data);
    setCampaigns(c.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    try {
      const { data } = await api.post("/leads", payload);
      toast.success("Lead created");
      setOpen(false);
      await load();
      nav(`/app/leads/${data.id}`);
    } catch { toast.error("Failed"); }
  };

  const filtered = leads.filter((l) => {
    if (status !== "all" && l.status !== status) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return (
      (l.customer_name || "").toLowerCase().includes(t) ||
      (l.email || "").toLowerCase().includes(t) ||
      (l.phone || "").toLowerCase().includes(t) ||
      (l.property_address || "").toLowerCase().includes(t)
    );
  });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">All records</div>
          <h1 className="font-heading text-4xl font-bold">Leads</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="leads-new-lead-btn">
          <Plus className="w-4 h-4 mr-1"/> New lead
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search name, email, phone, address" className="pl-9" data-testid="leads-search-input"/>
          </div>
          <div className="flex flex-wrap gap-1">
            <button onClick={()=>setStatus("all")} className={`text-xs px-3 py-1.5 rounded-md border ${status==="all" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"}`} data-testid="leads-filter-all">All</button>
            {STATUSES.map((s) => (
              <button
                key={s.key}
                onClick={()=>setStatus(s.key)}
                className={`text-xs px-3 py-1.5 rounded-md border ${status===s.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"}`}
                data-testid={`leads-filter-${s.key}`}
              >{s.label}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Service</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Value</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">AI</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const s = statusMeta(l.status);
                return (
                  <tr key={l.id} onClick={()=>nav(`/app/leads/${l.id}`)} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`leads-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{l.customer_name}</div>
                      <div className="text-xs text-zinc-500 truncate max-w-[220px]">{l.property_address}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{l.service_requested || "—"}</td>
                    <td className="px-4 py-3 text-zinc-600">
                      <div className="text-xs">{l.phone || "—"}</div>
                      <div className="text-xs text-zinc-500">{l.email || "—"}</div>
                    </td>
                    <td className="px-4 py-3"><span className="text-xs text-zinc-600 capitalize">{(l.lead_source||"").replace(/_/g," ")}</span></td>
                    <td className="px-4 py-3 font-mono-accent">${(l.estimated_value||0).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs border rounded-md ${s.color}`}>{s.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      {l.ai_score != null ? (
                        <span className="font-mono-accent text-xs px-1.5 py-0.5 rounded bg-orange-50 text-[#E04F00] border border-orange-200">{l.ai_score}</span>
                      ) : <span className="text-xs text-zinc-400">—</span>}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="text-center text-zinc-500 py-12">No leads match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <LeadFormDialog open={open} onOpenChange={setOpen} onSubmit={create} campaigns={campaigns} />
    </div>
  );
}
